import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { Users, Lock } from "lucide-react";
import UserPicker from "./UserPicker";
import ExternalDomainWarning from "./ExternalDomainWarning";
import { CustomLoading } from "components/CustomLoading";
import { useModalDismiss } from "./useModalDismiss";

interface CategoryRecord {
    id: string;
    name: string;
    description: string | null;
    color: string | null;
    icon: string | null;
    sortMode: string;
    accessLevel: string;
    ownerUserIds: string[];
    allowedUserIds: string[];
}

interface Props {
    categoryId: string;
    onSave: () => void;
    onCancel: () => void;
}

// Focused modal for changing just the category's access level (opened from the
// "Everyone"/"Invite only" chip in the category header). Refetches the full
// record because PUT /categories/{id} is a full replace.
export default function CategoryVisibilityModal({ categoryId, onSave, onCancel }: Props) {
    const [record, setRecord] = useState<CategoryRecord | null>(null);
    const [accessLevel, setAccessLevel] = useState("public");
    const [allowed, setAllowed] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    useEffect(() => {
        axios.get(`/api/manuals/categories/${categoryId}`)
            .then(res => {
                if (res.data.success) {
                    const item: CategoryRecord = res.data.item;
                    setRecord(item);
                    setAccessLevel(item.accessLevel || "public");
                    setAllowed(item.allowedUserIds || []);
                } else {
                    toast.error(res.data.message || "Failed to load category");
                    dismiss("cancel");
                }
            })
            .catch(() => {
                toast.error("Failed to load category");
                dismiss("cancel");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!record) return;
        setSaving(true);

        const payload = {
            name: record.name,
            description: record.description,
            color: record.color,
            icon: record.icon,
            sortMode: record.sortMode,
            accessLevel,
            ownerUserIds: record.ownerUserIds || [],
            allowedUserIds: accessLevel === "restricted" ? allowed : [],
        };

        axios.put(`/api/manuals/categories/${record.id}`, payload)
            .then(res => {
                if (res.data.success) {
                    toast.success("Visibility updated");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to update visibility");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to update visibility");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered scrollable backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>
                        Who can access {record ? `"${record.name}"` : "this category"}?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!record ? (
                        <div className="m-loading"><CustomLoading /></div>
                    ) : (
                        <>
                            <div className="mb-3">
                                <label className="m-label">Access level</label>
                                <div className="m-radio-group">
                                    <label className="m-radio">
                                        <input type="radio" name="visAccessLevel" value="public" checked={accessLevel === "public"} onChange={() => setAccessLevel("public")} />
                                        <Users size={14} style={{ marginRight: 4 }} /> <span>Everyone at National HealthClaim</span>
                                    </label>
                                    <label className="m-radio">
                                        <input type="radio" name="visAccessLevel" value="restricted" checked={accessLevel === "restricted"} onChange={() => setAccessLevel("restricted")} />
                                        <Lock size={14} style={{ marginRight: 4 }} /> <span>Only invited people</span>
                                    </label>
                                </div>
                            </div>

                            {accessLevel === "restricted" && (
                                <div className="mb-3">
                                    <label className="m-label">Allowed people</label>
                                    <UserPicker value={allowed} onChange={setAllowed} placeholder="Add user..." excludeIds={record.ownerUserIds || []} />
                                    <ExternalDomainWarning userIds={allowed} />
                                    <div className="m-hint">Owners also have access automatically.</div>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving || !record}>
                        {saving ? "Saving..." : "Save"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
